// ============= Imports =============
import React, { useMemo } from 'react';
import { Button, Modal } from '@/ui';
import { ErrorBoundary } from '@/components/common/ErrorBoundary/ErrorBoundary';
import { VISUAL_PROMPT_MESSAGES } from '@/constants/visualPrompts/messages';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { useVisualPromptWizard } from '@/hooks/visualPrompts/useVisualPromptWizard';
import { buildReviewContext } from '@/utils/visualPrompts/buildReviewContext';
import { saveChoicesForPrompt } from '@/utils/visualPrompts/visualPromptStorage';
import { VisualPromptPreview } from '../VisualPromptPreview';
import { VisualPromptProgress } from '../VisualPromptProgress';
import { Footer, FooterActions, ModalBody } from './styles';
import { renderWizardStep } from './renderWizardStep';
import type { VisualPromptWizardProps } from './types';

// ============= Component =============
export const VisualPromptWizard: React.FC<VisualPromptWizardProps> = ({
  isOpen,
  definition,
  onClose,
  onRun,
  isRunning = false,
  reviewContext: reviewContextBase,
}) => {
  const wizard = useVisualPromptWizard(definition);
  const { handleSuccess } = useWorkspaceErrorHandler();

  const reviewContext = useMemo(
    () =>
      buildReviewContext(wizard.answers, reviewContextBase?.workspaceName),
    [wizard.answers, reviewContextBase?.workspaceName],
  );

  const handleSaveChoices = () => {
    if (!definition) {
      return;
    }
    saveChoicesForPrompt(definition.id, wizard.answers);
    handleSuccess(VISUAL_PROMPT_MESSAGES.saveChoicesSuccess);
  };

  const handleClose = () => {
    onClose();
  };

  const handlePrimary = async () => {
    if (!definition) {
      return;
    }
    if (wizard.isReviewStep || wizard.isLastStep) {
      await onRun(definition, wizard.answers);
      return;
    }
    wizard.goNext();
  };

  const progressSteps = wizard.visibleSteps.map((s) => ({
    id: s.id,
    title: s.title,
  }));

  const primaryLabel =
    wizard.isReviewStep || wizard.isLastStep
      ? VISUAL_PROMPT_MESSAGES.runPrompt
      : VISUAL_PROMPT_MESSAGES.continue;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={definition?.title ?? 'Visual Prompt'}
      size="full"
      footer={
        <Footer>
          <Button
            variant="secondary"
            onClick={wizard.currentStepIndex > 0 ? wizard.goBack : handleClose}
            disabled={isRunning}
          >
            {wizard.currentStepIndex > 0
              ? VISUAL_PROMPT_MESSAGES.back
              : 'Cancel'}
          </Button>
          <FooterActions>
            <Button
              variant="primary"
              onClick={() => void handlePrimary()}
              disabled={isRunning || !wizard.canProceed()}
            >
              {isRunning
                ? VISUAL_PROMPT_MESSAGES.runningPrompt
                : primaryLabel}
            </Button>
          </FooterActions>
        </Footer>
      }
    >
      <ErrorBoundary>
        <ModalBody>
          {definition && (
            <>
              <VisualPromptProgress
                steps={progressSteps}
                currentIndex={wizard.currentStepIndex}
              />
              {wizard.currentStep &&
                renderWizardStep(
                  wizard.currentStep,
                  wizard.answers,
                  wizard.setAnswer,
                  wizard.stepError,
                  wizard.compiledPreview,
                  {
                    definition,
                    reviewContext,
                    onSaveChoices: handleSaveChoices,
                  },
                )}
              <VisualPromptPreview compiledPreview={wizard.compiledPreview} />
            </>
          )}
        </ModalBody>
      </ErrorBoundary>
    </Modal>
  );
};
